import {
  Box,
  Button,
  FormControl,
  Heading,
  HStack,
  Input,
  Modal,
  ScrollView,
  Select,
  Switch,
  Text,
  useToast,
  VStack,
  useColorModeValue,
  Divider,
  Spinner,
  Center
} from 'native-base';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Platform } from 'react-native';
import { IStore } from 'src/redux/reducers';
import { IGrain, IGrainProfileExtended } from 'src/models/GrainProfile';
import { getText } from 'src/utils/i18n';
import { isElectron, shouldUseLocalProcessing } from 'src/utils/miscUtils';
import * as GrainAPI from 'src/api/grain';
import * as UserAPI from 'src/api/user';
import * as GrainProfileActions from 'src/redux/actions/grainProfileAction';
import * as UserActions from 'src/redux/actions/userAction';
import * as AppActions from 'src/redux/actions/appAction';
import { containerStyle } from 'src/styles/containerStyle';
import MaterialIconByName from 'src/components/MaterialIconByName';
import { mergeThresholdInputs, resolveNumericInput } from 'src/utils/grainProfileNumericInputs';
import { RejectSelectionGroup } from 'src/components/RejectSelectionGroup';
import {
  buildRejectSelectionSessionConfig,
  getDefaultRejectSelectionConfig,
  getRejectOptions,
  getRejectSelectionSessionKey,
  REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID,
  resolveRejectSelectionConfig,
  resolveRejectSelectionSessionConfig
} from 'src/utils/rejectConfig';
import {
  getDefaultParticleDistributionSettings,
  PARTICLE_DISTRIBUTION_SETTING_NAMES
} from 'src/utils/particleDistributionSettings';
import {
  getNextActiveProfileId,
  shouldDeferCloudProfileDelete
} from 'src/utils/grainProfileState';

const confirmAction = async (title: string, message: string, confirmText = 'OK'): Promise<boolean> => {
  if (Platform.OS === 'web' && typeof window !== 'undefined' && typeof window.confirm === 'function') {
    return window.confirm(message);
  }

  return new Promise((resolve) => {
    Alert.alert(title, message, [
      { text: getText('cancel'), style: 'cancel', onPress: () => resolve(false) },
      { text: confirmText, style: 'destructive', onPress: () => resolve(true) }
    ]);
  });
};

const getVisibleThresholdEntries = (
  rejectionThresholds: Record<string, number> | undefined,
  grain: IGrain | undefined
) => {
  const mapping = grain?.rejectionMapping || grain?.rawJson?.rejectionMapping || {};
  return Object.entries(rejectionThresholds || {})
    .filter(([key]) => Boolean(mapping[key]))
    .map(([key, value]) => ({
      key,
      value,
      label: mapping[key] || key
    }));
};

const getDefaultRejectionThresholds = (
  grainId: string,
  grains: IGrain[],
  profiles: IGrainProfileExtended[]
) => {
  const grain = grains.find((item) => item.grainId === grainId);
  const mapping = grain?.rejectionMapping || grain?.rawJson?.rejectionMapping || {};
  const mappedKeys = Object.keys(mapping);
  const templateProfile =
    profiles.find((currentProfile) =>
      currentProfile.grainId === grainId
      && Object.keys(currentProfile.rejectionThresholds || {}).some((key) => mappedKeys.includes(key))
    )
    || profiles.find((currentProfile) => currentProfile.grainId === grainId);

  if (!templateProfile?.rejectionThresholds) {
    return {};
  }

  if (mappedKeys.length === 0) {
    return { ...templateProfile.rejectionThresholds };
  }

  return Object.fromEntries(
    Object.entries(templateProfile.rejectionThresholds).filter(([key]) => mappedKeys.includes(key))
  );
};

const buildManagedProfilePayload = (profile: Partial<IGrainProfileExtended>) => ({
  profileName: profile.profileName,
  brokenByShape: profile.brokenByShape,
  brokenThreshold: profile.brokenThreshold,
  particleDistributionRejectKeys: profile.particleDistributionRejectKeys,
  rejectedGrainsRejectKeys: profile.rejectedGrainsRejectKeys,
  len_calib: profile.len_calib,
  width_calib: profile.width_calib,
  rice_type: profile.rice_type,
  add_calib_r: profile.add_calib_r,
  add_calib_g: profile.add_calib_g,
  add_calib_b: profile.add_calib_b,
  csv_profile: profile.csv_profile,
  rejectionThresholds: profile.rejectionThresholds,
});

const isLocalOnlyProfile = (profile: IGrainProfileExtended) =>
  profile.profileType === 'CUSTOM' || String(profile.profileId || '').startsWith('custom_');

const getProfileOperationKey = (profile: Pick<IGrainProfileExtended, 'grainId' | 'profileId'>) =>
  `${profile.grainId}:${profile.profileId}`;

const PROFILE_OPERATION_TOAST_ID = 'profile-operation-toast';
const PROFILE_OPERATION_TOAST_DURATION_MS = 2500;
let profileOperationToastShowTimer: ReturnType<typeof setTimeout> | undefined;
let profileOperationToastCloseTimer: ReturnType<typeof setTimeout> | undefined;

const showProfileOperationToast = (
  toast: any,
  options: {
    description: string;
    status?: 'success' | 'error' | 'warning' | 'info';
    duration?: number;
  }
) => {
  if (!toast?.show) {
    return;
  }

  const duration = options.duration ?? PROFILE_OPERATION_TOAST_DURATION_MS;
  if (profileOperationToastShowTimer) {
    clearTimeout(profileOperationToastShowTimer);
  }
  if (profileOperationToastCloseTimer) {
    clearTimeout(profileOperationToastCloseTimer);
  }

  toast.closeAll?.();

  profileOperationToastShowTimer = setTimeout(() => {
    toast.closeAll?.();
    toast.show({
      id: PROFILE_OPERATION_TOAST_ID,
      description: options.description,
      status: options.status,
      duration: null
    });

    profileOperationToastCloseTimer = setTimeout(() => {
      toast.close?.(PROFILE_OPERATION_TOAST_ID);
      profileOperationToastCloseTimer = undefined;
    }, duration);
    profileOperationToastShowTimer = undefined;
  }, 0);
};

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  profile: IGrainProfileExtended | null;
  grain: IGrain | undefined;
  onSave: (profile: IGrainProfileExtended) => Promise<void>;
}

const EditProfileModal: React.FC<EditProfileModalProps> = ({
  isOpen,
  onClose,
  profile,
  grain,
  onSave
}) => {
  const dispatch = useDispatch();
  const userSettings = useSelector((store: IStore) => store.user.userSettings);
  const [editedProfile, setEditedProfile] = React.useState<IGrainProfileExtended | null>(null);
  const [brokenThresholdInput, setBrokenThresholdInput] = React.useState('');
  const [thresholdInputs, setThresholdInputs] = React.useState<Record<string, string>>({});
  const [particleDistributionRejectKeys, setParticleDistributionRejectKeys] = React.useState<string[]>([]);
  const [rejectedGrainsRejectKeys, setRejectedGrainsRejectKeys] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);

  React.useEffect(() => {
    if (profile) {
      setEditedProfile({ ...profile });
      setBrokenThresholdInput(String(profile.brokenThreshold ?? ''));
      setThresholdInputs(
        Object.fromEntries(
        Object.entries(profile.rejectionThresholds || {}).map(([key, value]) => [key, String(value)])
        )
      );
      const sessionKey = getRejectSelectionSessionKey(profile.grainId, profile.profileId);
      const hasProfileRejectConfig =
        Array.isArray(profile.particleDistributionRejectKeys)
        || Array.isArray(profile.rejectedGrainsRejectKeys)
        || Boolean(profile.rawJson?.rejectSelectionConfig);
      const profileRejectConfig = hasProfileRejectConfig
        ? resolveRejectSelectionConfig({ grain, profile })
        : null;
      const sessionRejectConfig = !profileRejectConfig
        ? resolveRejectSelectionSessionConfig({
          grain,
          profileId: profile.profileId,
          storedValue: userSettings?.[sessionKey]
        })
        : null;
      const defaultRejectConfig = getDefaultRejectSelectionConfig(grain);

      setParticleDistributionRejectKeys(
        profileRejectConfig?.particleDistributionRejectKeys
        || sessionRejectConfig?.particleDistribution.selectedKeys
        || defaultRejectConfig.particleDistributionRejectKeys
      );
      setRejectedGrainsRejectKeys(
        profileRejectConfig?.rejectedGrainsRejectKeys
        || sessionRejectConfig?.rejectedGrains.selectedKeys
        || defaultRejectConfig.rejectedGrainsRejectKeys
      );
    }
  }, [profile, grain, userSettings]);

  const rejectOptions = React.useMemo(() => getRejectOptions(grain, { localizeLabels: false }), [grain]);

  const visibleThresholdEntries = React.useMemo(
    () => getVisibleThresholdEntries(editedProfile?.rejectionThresholds, grain),
    [editedProfile?.rejectionThresholds, grain]
  );

  const handleSave = async () => {
    if (!editedProfile) return;
    setIsSaving(true);
    try {
      const sessionKey = getRejectSelectionSessionKey(editedProfile.grainId, editedProfile.profileId);
      const rejectSelectionConfig = buildRejectSelectionSessionConfig({
        grain,
        profileId: editedProfile.profileId,
        particleDistributionSelectedKeys: particleDistributionRejectKeys,
        rejectedGrainsSelectedKeys: rejectedGrainsRejectKeys,
        source: 'profile'
      });
      await onSave({
        ...editedProfile,
        brokenThreshold: resolveNumericInput(brokenThresholdInput, editedProfile.brokenThreshold),
        rejectionThresholds: mergeThresholdInputs(editedProfile.rejectionThresholds, thresholdInputs),
        particleDistributionRejectKeys,
        rejectedGrainsRejectKeys,
      });
      dispatch(UserActions.setUserSettingsValue({
        key: sessionKey,
        value: rejectSelectionConfig
      }));
      onClose();
    } catch {
      // The parent save handler already reports the error; keep the modal open.
    } finally {
      setIsSaving(false);
    }
  };

  const updateThreshold = (key: string, value: string) => {
    setThresholdInputs((current) => ({
      ...current,
      [key]: value
    }));
  };

  if (!editedProfile) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <Modal.Content maxWidth="800px">
        <Modal.CloseButton />
        <Modal.Header>
          <VStack space={1}>
            <Text fontSize="md" fontWeight="600">
              {getText('editProfileTitle')}
            </Text>
            <Text>{editedProfile.profileName}</Text>
            {grain?.name ? (
              <Text fontSize="xs" color="gray.500">
                {grain.name}
              </Text>
            ) : null}
          </VStack>
        </Modal.Header>
        <Modal.Body>
          <ScrollView maxH="500px">
            <VStack space={4}>
              <FormControl>
                <FormControl.Label>{getText('profileNameLabel')}</FormControl.Label>
                <Input
                  value={editedProfile.profileName}
                  onChangeText={(text) =>
                    setEditedProfile({ ...editedProfile, profileName: text })
                  }
                />
              </FormControl>

              <FormControl>
                <FormControl.Label>{getText('brokenThresholdLabel')}</FormControl.Label>
                <Input
                  value={brokenThresholdInput}
                  onChangeText={setBrokenThresholdInput}
                  keyboardType="numeric"
                />
              </FormControl>

              <FormControl>
                <HStack alignItems="center" space={2}>
                  <FormControl.Label flex={1}>{getText('brokenByShapeLabel')}</FormControl.Label>
                  <Switch
                    isChecked={editedProfile.brokenByShape}
                    onToggle={(value) =>
                      setEditedProfile({ ...editedProfile, brokenByShape: value })
                    }
                    />
                </HStack>
              </FormControl>

              <RejectSelectionGroup
                title="Particle Distribution Rejects"
                helperText="Controls which reject categories contribute to particle distribution outputs."
                options={rejectOptions}
                selectedKeys={particleDistributionRejectKeys}
                onChange={setParticleDistributionRejectKeys}
              />

              <RejectSelectionGroup
                title="Rejected Grains %wt"
                helperText="Controls which reject categories contribute to rejected-grains percentage outputs."
                options={rejectOptions}
                selectedKeys={rejectedGrainsRejectKeys}
                onChange={setRejectedGrainsRejectKeys}
              />

              {editedProfile.len_calib !== undefined && (
                <FormControl>
                  <FormControl.Label>{getText('lengthCalibrationLabel')}</FormControl.Label>
                  <Input
                    value={editedProfile.len_calib || ''}
                    onChangeText={(text) =>
                      setEditedProfile({ ...editedProfile, len_calib: text })
                    }
                  />
                </FormControl>
              )}

              {editedProfile.rice_type !== undefined && (
                <FormControl>
                  <FormControl.Label>{getText('riceTypeLabel')}</FormControl.Label>
                  <Input
                    value={editedProfile.rice_type || ''}
                    onChangeText={(text) =>
                      setEditedProfile({ ...editedProfile, rice_type: text })
                    }
                  />
                </FormControl>
              )}

              {visibleThresholdEntries.length > 0 ? <Divider my={2} /> : null}

              {visibleThresholdEntries.length > 0 ? (
                <Heading size="sm">{getText('rejectionThresholds')}</Heading>
              ) : null}
              <VStack space={2}>
                {visibleThresholdEntries.map(({ key, value, label }) => (
                  <FormControl key={key}>
                    <FormControl.Label>{label}</FormControl.Label>
                    <Input
                      value={thresholdInputs[key] ?? String(value)}
                      onChangeText={(text) => updateThreshold(key, text)}
                      keyboardType="numeric"
                    />
                  </FormControl>
                ))}
              </VStack>
            </VStack>
          </ScrollView>
        </Modal.Body>
        <Modal.Footer>
          <Button.Group space={2}>
            <Button variant="ghost" onPress={onClose} isDisabled={isSaving}>
              {getText('cancel')}
            </Button>
            <Button onPress={handleSave} isLoading={isSaving}>
              {getText('saveChanges')}
            </Button>
          </Button.Group>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

interface NewProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  grains: IGrain[];
  profiles: IGrainProfileExtended[];
  isOnline: boolean;
  useLocalProcessing: boolean;
  onSave: (grainId: string, profile: Partial<IGrainProfileExtended>) => Promise<void>;
}

const NewProfileModal: React.FC<NewProfileModalProps> = ({
  isOpen,
  onClose,
  grains,
  profiles,
  isOnline,
  useLocalProcessing,
  onSave
}) => {
  const dispatch = useDispatch();
  const [selectedGrainId, setSelectedGrainId] = React.useState('');
  const [profileName, setProfileName] = React.useState('');
  const [brokenThreshold, setBrokenThreshold] = React.useState('4.0');
  const [brokenByShape, setBrokenByShape] = React.useState(false);
  const [particleDistributionRejectKeys, setParticleDistributionRejectKeys] = React.useState<string[]>([]);
  const [rejectedGrainsRejectKeys, setRejectedGrainsRejectKeys] = React.useState<string[]>([]);
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedGrainProfiles, setSelectedGrainProfiles] = React.useState<IGrainProfileExtended[]>(profiles);
  const [isLoadingProfiles, setIsLoadingProfiles] = React.useState(false);
  const selectedGrain = React.useMemo(() => grains.find((grain) => grain.grainId === selectedGrainId), [grains, selectedGrainId]);
  const rejectOptions = React.useMemo(() => getRejectOptions(selectedGrain, { localizeLabels: false }), [selectedGrain]);

  React.useEffect(() => {
    if (!selectedGrainId) {
      return;
    }
    const defaultRejectConfig = getDefaultRejectSelectionConfig(selectedGrain);
    setParticleDistributionRejectKeys(defaultRejectConfig.particleDistributionRejectKeys);
    setRejectedGrainsRejectKeys(defaultRejectConfig.rejectedGrainsRejectKeys);
  }, [selectedGrainId, selectedGrain]);

  React.useEffect(() => {
    setSelectedGrainProfiles(profiles);
  }, [profiles]);

  React.useEffect(() => {
    if (!isOpen || !selectedGrainId) {
      setSelectedGrainProfiles([]);
      return;
    }

    let cancelled = false;

    const loadProfiles = async () => {
      setIsLoadingProfiles(true);
      try {
        let nextProfiles: IGrainProfileExtended[] = [];

        if (isElectron() && (!isOnline || useLocalProcessing)) {
          const electron = typeof window === 'undefined'
            ? undefined
            : (window as Window & {
              electron?: {
                getGrainProfiles?: (args: { grainId: string }) => Promise<IGrainProfileExtended[]>;
              };
            }).electron;

          if (electron?.getGrainProfiles) {
            nextProfiles = await electron.getGrainProfiles({ grainId: selectedGrainId });
          }
        } else if (isOnline) {
          nextProfiles = await GrainAPI.getGrainProfiles(selectedGrainId);
        }

        if (!cancelled) {
          setSelectedGrainProfiles(
            Array.isArray(nextProfiles)
              ? nextProfiles.filter((profile) => `${profile.grainId}` === `${selectedGrainId}`)
              : []
          );
        }
      } catch (error) {
        if (!cancelled) {
          console.warn('Failed to load default profiles for new grain profile:', error);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingProfiles(false);
        }
      }
    };

    loadProfiles();

    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedGrainId, isOnline, useLocalProcessing]);

  const handleSave = async () => {
    if (!selectedGrainId || !profileName.trim()) return;

    setIsSaving(true);
    try {
      const defaultRejectConfig = getDefaultRejectSelectionConfig(selectedGrain);
      const draftKey = getRejectSelectionSessionKey(selectedGrainId, REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID);
      const rejectSelectionConfig = buildRejectSelectionSessionConfig({
        grain: selectedGrain,
        profileId: REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID,
        particleDistributionSelectedKeys: particleDistributionRejectKeys.length > 0
          ? particleDistributionRejectKeys
          : defaultRejectConfig.particleDistributionRejectKeys,
        rejectedGrainsSelectedKeys: rejectedGrainsRejectKeys.length > 0
          ? rejectedGrainsRejectKeys
          : defaultRejectConfig.rejectedGrainsRejectKeys,
        source: 'default'
      });
      const newProfile: Partial<IGrainProfileExtended> = {
        profileName: profileName.trim(),
        brokenThreshold: parseFloat(brokenThreshold) || 4.0,
        brokenByShape,
        rejectionThresholds: getDefaultRejectionThresholds(selectedGrainId, grains, selectedGrainProfiles),
        particleDistributionRejectKeys: particleDistributionRejectKeys.length > 0
          ? particleDistributionRejectKeys
          : defaultRejectConfig.particleDistributionRejectKeys,
        rejectedGrainsRejectKeys: rejectedGrainsRejectKeys.length > 0
          ? rejectedGrainsRejectKeys
          : defaultRejectConfig.rejectedGrainsRejectKeys
      };

      dispatch(UserActions.setUserSettingsValue({
        key: draftKey,
        value: rejectSelectionConfig
      }));

      await onSave(selectedGrainId, newProfile);

      setSelectedGrainId('');
      setProfileName('');
      setBrokenThreshold('4.0');
      setBrokenByShape(false);
      setParticleDistributionRejectKeys([]);
      setRejectedGrainsRejectKeys([]);
      onClose();
    } catch {
      // The parent save handler already reports the error; keep the draft open.
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Content maxWidth="600px">
        <Modal.CloseButton />
        <Modal.Header>{getText('createNewProfile')}</Modal.Header>
        <Modal.Body>
          <VStack space={4}>
            <FormControl isRequired>
              <FormControl.Label>{getText('selectGrainTypeLabel')}</FormControl.Label>
              <Select
                selectedValue={selectedGrainId}
                onValueChange={setSelectedGrainId}
                placeholder={getText('selectGrainTypePlaceholder')}
              >
                {grains.map((grain) => (
                  <Select.Item
                    key={grain.grainId}
                    label={grain.name || `Grain ${grain.grainId}`}
                    value={grain.grainId}
                  />
                ))}
              </Select>
            </FormControl>

            {isLoadingProfiles ? (
              <Text fontSize="sm" color="gray.500">
                {getText('loadingGrainProfiles')}
              </Text>
            ) : null}

            <FormControl isRequired>
              <FormControl.Label>{getText('profileNameLabel')}</FormControl.Label>
              <Input
                value={profileName}
                onChangeText={setProfileName}
                placeholder={getText('profileNamePlaceholder')}
              />
            </FormControl>

            <FormControl>
              <FormControl.Label>{getText('brokenThresholdLabel')}</FormControl.Label>
              <Input
                value={brokenThreshold}
                onChangeText={setBrokenThreshold}
                keyboardType="numeric"
              />
            </FormControl>

            <FormControl>
              <HStack alignItems="center" space={2}>
                <FormControl.Label flex={1}>{getText('brokenByShapeLabel')}</FormControl.Label>
                <Switch isChecked={brokenByShape} onToggle={setBrokenByShape} />
              </HStack>
            </FormControl>

            <RejectSelectionGroup
              title="Particle Distribution Rejects"
              helperText="Controls which reject categories contribute to particle distribution outputs."
              options={rejectOptions}
              selectedKeys={particleDistributionRejectKeys}
              onChange={setParticleDistributionRejectKeys}
            />

            <RejectSelectionGroup
              title="Rejected Grains %wt"
              helperText="Controls which reject categories contribute to rejected-grains percentage outputs."
              options={rejectOptions}
              selectedKeys={rejectedGrainsRejectKeys}
              onChange={setRejectedGrainsRejectKeys}
            />

            <Text fontSize="sm" color="gray.500">
              {getText('defaultThresholdsInfo')}
            </Text>
          </VStack>
        </Modal.Body>
        <Modal.Footer>
          <Button.Group space={2}>
            <Button variant="ghost" onPress={onClose} isDisabled={isSaving}>
              {getText('cancel')}
            </Button>
            <Button
              onPress={handleSave}
              isLoading={isSaving}
              isDisabled={!selectedGrainId || !profileName.trim() || isLoadingProfiles}
            >
              {getText('createProfile')}
            </Button>
          </Button.Group>
        </Modal.Footer>
      </Modal.Content>
    </Modal>
  );
};

function CloudGrainProfileManagementPage({ navigation }: { navigation: any }) {
  const [grains, setGrains] = React.useState<IGrain[]>([]);
  const [profiles, setProfiles] = React.useState<IGrainProfileExtended[]>([]);
  const [selectedGrainId, setSelectedGrainId] = React.useState<string>('');
  const [isLoadingGrains, setIsLoadingGrains] = React.useState(true);
  const [isLoadingProfiles, setIsLoadingProfiles] = React.useState(false);
  const [editingProfile, setEditingProfile] = React.useState<IGrainProfileExtended | null>(null);
  const [isEditModalOpen, setIsEditModalOpen] = React.useState(false);
  const [isNewModalOpen, setIsNewModalOpen] = React.useState(false);
  const [deletingProfileIds, setDeletingProfileIds] = React.useState<Record<string, boolean>>({});

  const toast = useToast() as any;
  const toastRef = React.useRef(toast);
  const dispatch = useDispatch();
  const isOnline = useSelector((store: IStore) => store.app.isOnline);
  const preferredProcessingMode = useSelector((store: IStore) => store.settings.preferredProcessingMode);
  const locale = useSelector((store: IStore) => store.app.locale);
  const activeGrainId = useSelector((store: IStore) => store.grainType.grainId);
  const profileId = useSelector((store: IStore) => store.grainProfile.profileId);
  const userSettings = useSelector((store: IStore) => store.user.userSettings);
  const username = useSelector((store: IStore) => store.user.userProfile.response?.username);
  const orgId = useSelector((store: IStore) => store.user.userProfile.response?.org?.orgId);
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const secondaryTextColor = useColorModeValue('gray.600', 'gray.400');
  const browserOnline =
    typeof window !== 'undefined' && typeof window.navigator !== 'undefined'
      ? window.navigator.onLine
      : true;
  const effectiveOnline = isOnline !== false && browserOnline !== false;
  const useLocalProcessing = shouldUseLocalProcessing(preferredProcessingMode, effectiveOnline);
  const isLoading = isLoadingGrains || isLoadingProfiles;
  const profileIdRef = React.useRef(profileId);
  const profilesRequestIdRef = React.useRef(0);
  const profilesRefreshPromiseRef = React.useRef<Promise<void> | null>(null);
  const profilesRefreshGrainIdRef = React.useRef<string | null>(null);
  const deletingProfileIdsRef = React.useRef<Set<string>>(new Set());

  React.useEffect(() => {
    toastRef.current = toast;
  }, [toast]);

  React.useEffect(() => {
    profileIdRef.current = profileId;
  }, [profileId]);

  React.useLayoutEffect(() => {
    navigation?.setOptions?.({ title: getText('grainProfilesTitle') });
  }, [navigation, locale]);

  const loadGrains = React.useCallback(async () => {
    setIsLoadingGrains(true);
    try {
      if (isElectron() && (!isOnline || useLocalProcessing)) {
        const electron = (window as any).electron;
        if (!electron?.getGrains) {
          throw new Error('Electron API not available');
        }

        const grainsData: IGrain[] = await electron.getGrains();
        setGrains(grainsData);

        if (grainsData.length > 0) {
          setSelectedGrainId((current) => {
            const currentStillExists = current && grainsData.some((grain) => grain.grainId === current);
            return currentStillExists ? current : grainsData[0].grainId;
          });
        }
        return;
      }

      if (isOnline) {
        const grainsData = await GrainAPI.getAllGrains();
        setGrains(grainsData);

        if (grainsData.length > 0) {
          setSelectedGrainId((current) => {
            const currentStillExists = current && grainsData.some((grain) => grain.grainId === current);
            return currentStillExists ? current : grainsData[0].grainId;
          });
        }
        return;
      }

      throw new Error('No API or local storage available');
    } catch (error) {
      console.error('Error loading grain list:', error);
      toastRef.current.show({
        description: getText('loadingGrainProfiles'),
        status: 'error'
      });
    } finally {
      setIsLoadingGrains(false);
    }
  }, [isOnline, useLocalProcessing]);

  const loadProfilesForSelectedGrain = React.useCallback(
    async (grainId: string) => {
      if (
        profilesRefreshPromiseRef.current
        && profilesRefreshGrainIdRef.current === grainId
      ) {
        return profilesRefreshPromiseRef.current;
      }

      const refreshPromise = (async () => {
      const requestId = ++profilesRequestIdRef.current;

      if (!grainId) {
        setProfiles([]);
        return;
      }

      setIsLoadingProfiles(true);
      try {
        let nextProfiles: IGrainProfileExtended[] = [];

        if (isElectron() && (!isOnline || useLocalProcessing)) {
          const electron = (window as any).electron;
          if (!electron?.getGrainProfiles) {
            throw new Error('Electron API not available');
          }
          nextProfiles = await electron.getGrainProfiles({ grainId });
        } else if (isOnline) {
          nextProfiles = await GrainAPI.getGrainProfiles(grainId);
        } else {
          throw new Error('No API or local storage available');
        }

        const filteredProfiles = Array.isArray(nextProfiles)
          ? nextProfiles.filter((profile) => `${profile.grainId}` === `${grainId}`)
          : [];

        if (requestId !== profilesRequestIdRef.current) {
          return;
        }

        setProfiles(filteredProfiles);
        if (grainId === activeGrainId) {
          dispatch(GrainProfileActions.setGrainProfiles(filteredProfiles));
          const nextProfileId = getNextActiveProfileId({
            activeGrainId,
            mutationGrainId: grainId,
            currentProfileId: profileIdRef.current,
            nextProfiles: filteredProfiles
          });
          if (nextProfileId !== profileIdRef.current) {
            profileIdRef.current = nextProfileId;
            dispatch(GrainProfileActions.setProfileId(nextProfileId));
          }
        }
      } catch (error) {
        if (requestId !== profilesRequestIdRef.current) {
          return;
        }
        console.error('Error loading grain profiles:', error);
        toastRef.current.show({
          description: getText('loadingGrainProfiles'),
          status: 'error'
        });
      } finally {
        if (requestId === profilesRequestIdRef.current) {
          setIsLoadingProfiles(false);
        }
      }
      })();

      profilesRefreshPromiseRef.current = refreshPromise;
      profilesRefreshGrainIdRef.current = grainId;

      try {
        return await refreshPromise;
      } finally {
        if (profilesRefreshPromiseRef.current === refreshPromise) {
          profilesRefreshPromiseRef.current = null;
          profilesRefreshGrainIdRef.current = null;
        }
      }
    },
    [activeGrainId, dispatch, isOnline, useLocalProcessing]
  );

  const applyProfilesForGrain = React.useCallback(
    (grainId: string, updater: (current: IGrainProfileExtended[]) => IGrainProfileExtended[]) => {
      profilesRequestIdRef.current += 1;
      setProfiles((currentProfiles) => {
        const nextProfiles = updater(currentProfiles)
          .filter((profile) => `${profile.grainId}` === `${grainId}`)
          .sort((a, b) => String(a.profileName || '').localeCompare(String(b.profileName || '')));
        const otherProfiles = currentProfiles.filter((profile) => `${profile.grainId}` !== `${grainId}`);
        const combinedProfiles = [...otherProfiles, ...nextProfiles];

        if (grainId === activeGrainId) {
          dispatch(GrainProfileActions.setGrainProfiles(nextProfiles));
          const nextProfileId = getNextActiveProfileId({
            activeGrainId,
            mutationGrainId: grainId,
            currentProfileId: profileIdRef.current,
            nextProfiles
          });
          if (nextProfileId !== profileIdRef.current) {
            profileIdRef.current = nextProfileId;
            dispatch(GrainProfileActions.setProfileId(nextProfileId));
          }
        }

        return combinedProfiles;
      });
    },
    [activeGrainId, dispatch]
  );

  const upsertProfileForGrain = React.useCallback(
    (profile: IGrainProfileExtended) => {
      applyProfilesForGrain(profile.grainId, (currentProfiles) => {
        const sameGrainProfiles = currentProfiles.filter((item) => `${item.grainId}` === `${profile.grainId}`);
        const existingIndex = sameGrainProfiles.findIndex((item) => item.profileId === profile.profileId);

        if (existingIndex === -1) {
          return [...sameGrainProfiles, profile];
        }

        return sameGrainProfiles.map((item, index) =>
          index === existingIndex ? { ...item, ...profile } : item
        );
      });
    },
    [applyProfilesForGrain]
  );

  const removeProfileForGrain = React.useCallback(
    (profile: IGrainProfileExtended) => {
      applyProfilesForGrain(profile.grainId, (currentProfiles) =>
        currentProfiles.filter((item) =>
          `${item.grainId}` !== `${profile.grainId}` || item.profileId !== profile.profileId
        )
      );
    },
    [applyProfilesForGrain]
  );

  React.useEffect(() => {
    loadGrains();
  }, [loadGrains]);

  React.useEffect(() => {
    if (!selectedGrainId) {
      return;
    }

    loadProfilesForSelectedGrain(selectedGrainId);
  }, [loadProfilesForSelectedGrain, selectedGrainId]);

  const handleEditProfile = (profile: IGrainProfileExtended) => {
    setEditingProfile(profile);
    setIsEditModalOpen(true);
  };

  const handleSaveProfile = async (profile: IGrainProfileExtended) => {
    try {
      const payload = buildManagedProfilePayload(profile);

      if (isElectron()) {
        const electron = (window as any).electron;
        if (!electron) {
          throw new Error('Electron API not available');
        }

        if (useLocalProcessing) {
          const localResult = await electron.updateGrainProfile({
            grainId: profile.grainId,
            profileId: profile.profileId,
            profileData: {
              ...payload,
              ownerUsername: profile.ownerUsername || username,
              ownerOrgId: profile.ownerOrgId || orgId,
              syncStatus: 'pending',
              syncError: null
            }
          });
          upsertProfileForGrain(localResult?.profile || profile);
          if (effectiveOnline) {
            dispatch(AppActions.syncLocalJobsNow());
          }
        } else if (effectiveOnline && !isLocalOnlyProfile(profile)) {
          const cloudProfile = await GrainAPI.updateGrainProfile(profile.grainId, profile.profileId, payload);
          const localResult = await electron.updateGrainProfile({
            grainId: cloudProfile.grainId,
            profileId: cloudProfile.profileId,
            profileData: {
              ...cloudProfile,
              ownerUsername: cloudProfile.ownerUsername || username,
              ownerOrgId: cloudProfile.ownerOrgId || orgId
            }
          });
          upsertProfileForGrain(localResult?.profile || cloudProfile);
        } else {
          const localResult = await electron.updateGrainProfile({
            grainId: profile.grainId,
            profileId: profile.profileId,
            profileData: isLocalOnlyProfile(profile)
              ? {
                  ...payload,
                  ownerUsername: profile.ownerUsername || username,
                  ownerOrgId: profile.ownerOrgId || orgId
                }
              : {
                  ...payload,
                  ownerUsername: profile.ownerUsername || username,
                  ownerOrgId: profile.ownerOrgId || orgId,
                  syncStatus: 'pending',
                  syncError: null
                }
          });
          upsertProfileForGrain(localResult?.profile || profile);
          if (effectiveOnline && !isLocalOnlyProfile(profile)) {
            dispatch(AppActions.syncLocalJobsNow());
          }
        }

        showProfileOperationToast(toast, {
          description: getText('profileUpdatedSuccess'),
          status: 'success'
        });

        return;
      }

      if (isOnline) {
        try {
          const updatedProfile = await GrainAPI.updateGrainProfile(profile.grainId, profile.profileId, payload);
          upsertProfileForGrain(updatedProfile);

          showProfileOperationToast(toast, {
            description: getText('profileUpdatedSuccess'),
            status: 'success'
          });
          return;
        } catch (apiError) {
          console.log('API update failed, trying local fallback:', apiError);
        }
      }

      throw new Error('No API or local storage available');
    } catch (error) {
      console.error('Error updating profile:', error);
      showProfileOperationToast(toast, {
        description: getText('updateProfileFailed'),
        status: 'error',
        duration: 4000
      });
      throw error;
    }
  };

  const handleCreateProfile = async (grainId: string, profileData: Partial<IGrainProfileExtended>) => {
    const initializeParticleDistributionSettings = async (createdProfileId?: string) => {
      if (!createdProfileId || !username) {
        return;
      }

      await Promise.all(PARTICLE_DISTRIBUTION_SETTING_NAMES.map(async (settingName) => {
        const value = getDefaultParticleDistributionSettings(settingName);
        const settingsKey = `${orgId || ''}:${grainId || ''}:${createdProfileId || ''}:${settingName}`;

        if (isElectron() && (window as any).electron?.upsertLocalProfileSetting) {
          await (window as any).electron.upsertLocalProfileSetting({
            username,
            orgId,
            grainId,
            profileId: createdProfileId,
            settingName,
            value,
            syncStatus: useLocalProcessing ? 'pending' : 'synced',
            lastSyncedValue: useLocalProcessing ? undefined : value
          });
        }

        if (!useLocalProcessing && effectiveOnline) {
          await UserAPI.updateUserSettings({
            username,
            orgId,
            grainId,
            profileId: createdProfileId,
            settingName,
            newSetting: value
          });
        }

        dispatch(UserActions.setUserSettingsValue({ key: settingsKey, value }));
      }));
    };

    const initializeSafely = async (createdProfileId?: string) => {
      try {
        await initializeParticleDistributionSettings(createdProfileId);
      } catch (error) {
        console.warn('Failed to initialize particle distribution settings for new profile', error);
      }
    };

    try {
      if (isElectron()) {
        const electron = (window as any).electron;
        if (!electron) {
          throw new Error('Electron API not available');
        }

        if (!useLocalProcessing && effectiveOnline) {
          const cloudProfile = await GrainAPI.createGrainProfile(grainId, profileData as any);
          const localResult = await electron.createGrainProfile({
            grainId: cloudProfile.grainId,
            profileData: {
              ...cloudProfile,
              ownerUsername: cloudProfile.ownerUsername || username,
              ownerOrgId: cloudProfile.ownerOrgId || orgId
            }
          });
          const createdProfileId = localResult?.profileId || cloudProfile.profileId;
          await initializeSafely(createdProfileId);
          upsertProfileForGrain(localResult?.profile || cloudProfile);

          showProfileOperationToast(toast, {
            description: getText('profileCreatedSuccess'),
            status: 'success'
          });

          const draftKey = getRejectSelectionSessionKey(grainId, REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID);
          const draftConfig = userSettings?.[draftKey];
          if (draftConfig && createdProfileId) {
            dispatch(UserActions.setUserSettingsValue({
              key: getRejectSelectionSessionKey(grainId, createdProfileId),
              value: draftConfig
            }));
          }

          return;
        }

        const localResult = await electron.createGrainProfile({
          grainId,
          profileData: {
            ...profileData,
            ownerUsername: username,
            ownerOrgId: orgId,
            syncStatus: 'pending',
            syncError: null
          }
        });
        const createdProfileId = localResult?.profileId || localResult?.profile?.profileId;
        await initializeSafely(createdProfileId);
        if (localResult?.profile) {
          upsertProfileForGrain(localResult.profile);
        }

        showProfileOperationToast(toast, {
          description: getText('profileCreatedSuccess'),
          status: 'success'
        });

        const draftKey = getRejectSelectionSessionKey(grainId, REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID);
        const draftConfig = userSettings?.[draftKey];
        if (draftConfig && createdProfileId) {
          dispatch(UserActions.setUserSettingsValue({
            key: getRejectSelectionSessionKey(grainId, createdProfileId),
            value: draftConfig
          }));
        }

        if (effectiveOnline) {
          dispatch(AppActions.syncLocalJobsNow());
        }
        return;
      }

      if (isOnline) {
        try {
          const createdProfile = await GrainAPI.createGrainProfile(grainId, profileData as any);
          await initializeSafely(createdProfile?.profileId);
          if (createdProfile) {
            upsertProfileForGrain(createdProfile);
          }

          showProfileOperationToast(toast, {
            description: getText('profileCreatedSuccess'),
            status: 'success'
          });

          const draftKey = getRejectSelectionSessionKey(grainId, REJECT_SELECTION_SESSION_DRAFT_PROFILE_ID);
          const draftConfig = userSettings?.[draftKey];
          if (draftConfig && createdProfile?.profileId) {
            dispatch(UserActions.setUserSettingsValue({
              key: getRejectSelectionSessionKey(grainId, createdProfile.profileId),
              value: draftConfig
            }));
          }

          return;
        } catch (apiError) {
          console.log('API create failed, trying local fallback:', apiError);
        }
      }

      throw new Error('No API or local storage available');
    } catch (error) {
      console.error('Error creating profile:', error);
      showProfileOperationToast(toast, {
        description: getText('createProfileFailed'),
        status: 'error',
        duration: 4000
      });
      throw error;
    }
  };

  const handleDeleteProfile = async (profile: IGrainProfileExtended) => {
    const operationKey = getProfileOperationKey(profile);
    if (deletingProfileIdsRef.current.has(operationKey)) {
      return;
    }

    const confirmed = await confirmAction(
      getText('deleteProfileTitle'),
      getText('deleteProfileConfirmation', { profileName: profile.profileName }),
      getText('delete')
    );
    if (!confirmed) {
      return;
    }

    deletingProfileIdsRef.current.add(operationKey);
    setDeletingProfileIds((current) => ({
      ...current,
      [operationKey]: true
    }));

    try {
      if (isElectron()) {
        const electron = (window as any).electron;
        if (!electron) {
          throw new Error('Electron API not available');
        }

        if (effectiveOnline && !isLocalOnlyProfile(profile)) {
          try {
            await GrainAPI.deleteGrainProfile(profile.grainId, profile.profileId);
          } catch (apiError) {
            if (!GrainAPI.isGrainProfileNotFoundError(apiError)) {
              throw apiError;
            }
          }
        }

        const deferCloudDelete = shouldDeferCloudProfileDelete({
          isOnline: effectiveOnline,
          profile
        });
        await electron.deleteGrainProfile({
          grainId: profile.grainId,
          profileId: profile.profileId,
          deferCloudDelete,
          profileData: {
            ...profile,
            ownerUsername: profile.ownerUsername || username,
            ownerOrgId: profile.ownerOrgId || orgId
          }
        });
        removeProfileForGrain(profile);

        showProfileOperationToast(toast, {
          description: getText('profileDeletedSuccess'),
          status: 'success'
        });
        return;
      }

      if (isOnline) {
        try {
          await GrainAPI.deleteGrainProfile(profile.grainId, profile.profileId);
          removeProfileForGrain(profile);

          showProfileOperationToast(toast, {
            description: getText('profileDeletedSuccess'),
            status: 'success'
          });
          return;
        } catch (apiError) {
          if (GrainAPI.isNonRetryableGrainProfileError(apiError)) {
            console.log('API delete failed with non-retryable response:', apiError);
            throw apiError;
          }
          console.log('API delete failed, trying local fallback:', apiError);
        }
      }

      throw new Error('No API or local storage available');
    } catch (error) {
      console.error('Error deleting profile:', error);
      showProfileOperationToast(toast, {
        description: getText('deleteProfileFailed'),
        status: 'error',
        duration: 4000
      });
    } finally {
      deletingProfileIdsRef.current.delete(operationKey);
      setDeletingProfileIds((current) => {
        const next = { ...current };
        delete next[operationKey];
        return next;
      });
    }
  };

  const filteredProfiles = React.useMemo(() => {
    if (!selectedGrainId) return [];
    return profiles.filter((currentProfile) => currentProfile.grainId === selectedGrainId);
  }, [profiles, selectedGrainId]);

  const selectedGrain = React.useMemo(
    () => grains.find((currentGrain) => currentGrain.grainId === selectedGrainId),
    [grains, selectedGrainId]
  );

  if (isLoading) {
    return (
      <Box variant="main" style={containerStyle.mainContainer}>
        <Center flex={1}>
          <Spinner size="lg" />
          <Text mt={4}>{getText('loadingGrainProfiles')}</Text>
        </Center>
      </Box>
    );
  }

  return (
    <Box variant="main" style={containerStyle.mainContainer}>
      <ScrollView>
        <VStack p={isElectron() ? 8 : 4} space={isElectron() ? 6 : 4} maxW={isElectron() ? '1000px' : '100%'} alignSelf="center" w="100%">
          <HStack justifyContent="space-between" alignItems="center" flexWrap="wrap">
            <VStack space={1} flex={1}>
              <Heading size="lg">{getText('grainProfileManagement')}</Heading>
              <Text fontSize="sm" color={secondaryTextColor}>
                {getText('grainProfileManagementDescription')}
              </Text>
            </VStack>
            <Button
              onPress={() => setIsNewModalOpen(true)}
              size={isElectron() ? 'md' : 'sm'}
              leftIcon={<MaterialIconByName name="plus" size="sm" />}
            >
              {getText('newProfile')}
            </Button>
          </HStack>

          <FormControl>
            <FormControl.Label>{getText('selectGrainTypeLabel')}</FormControl.Label>
            <Select
              selectedValue={selectedGrainId}
              onValueChange={setSelectedGrainId}
              placeholder={getText('selectGrainTypePlaceholder')}
            >
              {grains.map((grain) => (
                <Select.Item
                  key={grain.grainId}
                  label={grain.name || `Grain ${grain.grainId}`}
                  value={grain.grainId}
                />
              ))}
            </Select>
          </FormControl>

          {selectedGrain && (
            <Box bg={cardBg} borderWidth={1} borderColor={borderColor} borderRadius="xl" p={isElectron() ? 6 : 4} shadow={isElectron() ? '2' : '0'}>
              <VStack space={3}>
                <HStack justifyContent="space-between" alignItems="center">
                  <Heading size="sm">
                    {selectedGrain.name} {getText('profilesLabel')} ({filteredProfiles.length})
                  </Heading>
                </HStack>

                {filteredProfiles.length === 0 ? (
                  <Text color={secondaryTextColor}>{getText('noProfilesForGrain')}</Text>
                ) : (
                  <VStack space={2}>
                    {filteredProfiles.map((profile) => {
                      const profileType = profile.profileType;
                      const isCustom =
                        typeof profileType === 'string'
                          ? profileType === 'USER' || profileType === 'ORG'
                          : profile.profileId.startsWith('custom_');
                      const canEdit = profile.editable ?? true;
                      const canDelete =
                        canEdit
                        && (typeof profileType === 'string'
                          ? profileType === 'USER' || profileType === 'ORG'
                          : isCustom);
                      const badgeLabel =
                        typeof profileType === 'string'
                          ? (profileType.startsWith('SCANNER_') ? 'SCANNER' : profileType)
                          : (isCustom ? 'CUSTOM' : undefined);
                      const isDeletingProfile = Boolean(deletingProfileIds[getProfileOperationKey(profile)]);

                      return (
                        <Box
                          key={profile.profileId}
                          bg={cardBg}
                          borderWidth={1}
                          borderColor={borderColor}
                          borderRadius="md"
                          p={3}
                        >
                          <HStack justifyContent="space-between" alignItems="center" space={2}>
                            <VStack flex={1}>
                              <HStack alignItems="center" space={2} flexWrap="wrap">
                                <Text fontWeight="600">{profile.profileName}</Text>
                                {badgeLabel && (
                                  <Box
                                    px={2}
                                    py={0.5}
                                    bg="blue.100"
                                    borderRadius="md"
                                  >
                                    <Text fontSize="xs" color="blue.800">
                                      {badgeLabel === 'CUSTOM' ? getText('customLabel') : badgeLabel}
                                    </Text>
                                  </Box>
                                )}
                              </HStack>
                              <Text fontSize="sm" color={secondaryTextColor}>
                                {getText('brokenLabel')}: {profile.brokenThreshold} | {getText('shapeLabel')}: {profile.brokenByShape ? getText('yes') : getText('no')}
                              </Text>
                              <Text fontSize="xs" color={secondaryTextColor}>
                                {getText('profileIdLabel')}: {profile.profileId}
                              </Text>
                            </VStack>
                            <HStack space={2}>
                              <Button
                                size="sm"
                                variant="outline"
                                isDisabled={!canEdit || isDeletingProfile}
                                onPress={() => handleEditProfile(profile)}
                              >
                                {getText('edit')}
                              </Button>
                              {canDelete && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  colorScheme="danger"
                                  isLoading={isDeletingProfile}
                                  isLoadingText="Deleting..."
                                  isDisabled={isDeletingProfile}
                                  onPress={() => handleDeleteProfile(profile)}
                                >
                                  {getText('delete')}
                                </Button>
                              )}
                            </HStack>
                          </HStack>
                        </Box>
                      );
                    })}
                  </VStack>
                )}
              </VStack>
            </Box>
          )}
        </VStack>
      </ScrollView>

      <EditProfileModal
        isOpen={isEditModalOpen}
        onClose={() => {
          setIsEditModalOpen(false);
          setEditingProfile(null);
        }}
        profile={editingProfile}
        grain={selectedGrain}
        onSave={handleSaveProfile}
      />

      <NewProfileModal
        isOpen={isNewModalOpen}
        onClose={() => setIsNewModalOpen(false)}
        grains={grains}
        profiles={profiles}
        isOnline={effectiveOnline}
        useLocalProcessing={useLocalProcessing}
        onSave={handleCreateProfile}
      />
    </Box>
  );
}

export default function GrainProfileManagementPage(props: { navigation: any }) {
  const locale = useSelector((store: IStore) => store.app.locale);

  React.useLayoutEffect(() => {
    props.navigation?.setOptions?.({ title: getText('grainProfilesTitle') });
  }, [props.navigation, locale]);

  return <CloudGrainProfileManagementPage {...props} />;
}
